import type { ReviewRating } from '@yorukiri/shared';
import { useTranslation } from '@yorukiri/i18n';
import { colors } from '@yorukiri/ui/tokens';

type RateConfig = {
  id: ReviewRating;
  kbd: string;
  interval: string;
  tone: 'again' | 'hard' | 'good' | 'easy';
};

const configs: RateConfig[] = [
  { id: 'again', kbd: '1', interval: '<1m', tone: 'again' },
  { id: 'hard', kbd: '2', interval: '6m', tone: 'hard' },
  { id: 'good', kbd: '3', interval: '10m', tone: 'good' },
  { id: 'easy', kbd: '4', interval: '4d', tone: 'easy' },
];

const toneColor: Record<RateConfig['tone'], string> = {
  again: colors.accent,
  hard: colors.warn,
  good: colors.ink,
  easy: colors.good,
};

export function RateButtons({ onRate }: { onRate: (r: ReviewRating) => void }) {
  const { t } = useTranslation('review');
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 180px)',
        gap: 10,
        maxWidth: 780,
        width: '100%',
      }}
    >
      {configs.map((c) => (
        <button
          key={c.id}
          onClick={() => onRate(c.id)}
          style={{
            background: colors.surface,
            border: `1px solid ${colors.line2}`,
            borderRadius: 10,
            padding: '14px 16px',
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
            transition: 'transform 0.15s, border-color 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colors.ink;
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 8px 16px -8px rgba(24,22,15,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = colors.line2;
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 500, color: toneColor[c.tone] }}>
              {t(`rate.${c.id}`)}
            </span>
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: 10,
                color: colors.ink3,
                background: 'rgba(24,22,15,0.05)',
                padding: '1px 6px',
                borderRadius: 3,
              }}
            >
              {c.kbd}
            </span>
          </div>
          <div
            style={{
              fontVariantNumeric: 'tabular-nums',
              fontSize: 11,
              color: colors.ink3,
              letterSpacing: '0.04em',
            }}
          >
            <b
              style={{
                fontWeight: 400,
                fontSize: 18,
                color: colors.ink,
                letterSpacing: '-0.01em',
                marginRight: 4,
              }}
            >
              {c.interval}
            </b>
            {t('rate.next')}
          </div>
        </button>
      ))}
    </div>
  );
}
